import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Building2, CalendarDays, ClipboardCheck,
  UserCircle, LogOut, Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolveAvatar } from "@/lib/api";
import { toast } from "sonner";

const BIHAR_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_workforce-dashboard-73/artifacts/vbcjdbyb_image.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true },
  { to: "/directory", label: "Employee Directory", icon: Users, testid: "nav-directory-link", adminOnly: true },
  { to: "/departments", label: "Departments", icon: Building2, testid: "nav-departments-link" },
  { to: "/leaves", label: "Leave Management", icon: CalendarDays, testid: "nav-leaves-link" },
  { to: "/attendance", label: "Attendance", icon: ClipboardCheck, testid: "nav-attendance-link" },
  { to: "/profile", label: "My Profile", icon: UserCircle, testid: "nav-profile-link" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const visibleNav = NAV.filter((n) => !n.adminOnly || user?.role === "admin");

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out successfully");
    navigate("/login");
  };

  const current = NAV.find((n) => (n.end ? location.pathname === "/" : location.pathname.startsWith(n.to)));

  const SidebarContent = () => (
    <>
      <div className="px-6 py-6 flex items-center gap-3 border-b border-white/10">
        <div className="w-11 h-11 rounded-full bg-white border border-white/20 flex items-center justify-center shrink-0 overflow-hidden p-1">
          <img src={BIHAR_LOGO} alt="Government of Bihar" className="w-full h-full object-contain" />
        </div>
        <div>
          <div className="font-heading text-xl font-extrabold leading-none">HRMS</div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-white/60 mt-1">Govt. of Bihar</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-accent text-accent-foreground shadow-sm" : "text-white/75 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <n.icon className="w-[18px] h-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10">
        <button
          data-testid="user-logout-button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white transition-colors"
        >
          <LogOut className="w-[18px] h-[18px]" /> Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 w-64 bg-primary text-primary-foreground flex-col z-40">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-primary text-primary-foreground flex flex-col">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-card/90 backdrop-blur-xl border-b border-border px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="mobile-menu-toggle">
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-heading text-lg sm:text-xl font-bold text-foreground">{current?.label || "Dashboard"}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-foreground leading-tight" data-testid="header-user-name">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{user?.role}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold text-sm overflow-hidden">
              {user?.avatar ? <img src={resolveAvatar(user.avatar)} alt={user.name} className="w-full h-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || "U")}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
